import { Principle_Config, Subprinciple_Config } from 'src/@types/graphql'
import { useUserLocale } from 'src/user/UserContext'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import { EditIcon, MoveDownIcon, MoveUpIcon } from 'src/shared/icons/Icons'
import { S } from 'src/shared/styled/S'
import React from 'react'
import { ROUTES } from 'src/routing/routes'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'

interface PrincipleSortListProps {
  principlesSorting: Principle_Config[]
  onSwitchPrinciplePosition: (pc1: Principle_Config, pc2: Principle_Config) => void
  onSwitchSubprinciplePosition: (spc1: Subprinciple_Config, spc2: Subprinciple_Config) => void
  isReorderingLocked: boolean
}

export const PrinciplesConfigurationSortList = ({
  principlesSorting,
  onSwitchPrinciplePosition,
  onSwitchSubprinciplePosition,
  isReorderingLocked = false,
}: PrincipleSortListProps) => {
  const locale = useUserLocale()
  const navigate = useDelayedNavigate()

  // handlers
  const getSubprinciplesSorting = (principle: Principle_Config) => {
    return principle.subprinciple_configs
  }

  const onEditPrincipleClick = (e: React.MouseEvent<HTMLButtonElement>, principleId: number) => {
    e.stopPropagation()

    const route = ROUTES.PrinciplesConfigurationIndex.nested.PrinciplesConfigurationPrinciplesEdit.params({
      principleId,
    })
    navigate(route, { state: { navigatedFromSortListView: true } })
  }

  const onEditSubPrincipleClick = (e: React.MouseEvent<HTMLButtonElement>, subprincipleId: number) => {
    e.stopPropagation()

    const route = ROUTES.PrinciplesConfigurationIndex.nested.PrinciplesConfigurationSubprinciplesEdit.params({
      subprincipleId,
    })
    navigate(route, { state: { navigatedFromSortListView: true } })
  }

  // JSX
  const renderPrincipleHeader = (
    pc: Principle_Config,
    pcIndex: number,
    principlesSorting: Principle_Config[],
    locale: string,
    isReorderingLocked: boolean,
    onSwitchPrinciplePosition: (a: Principle_Config, b: Principle_Config) => void,
  ) => (
    <S.ConfigurationSortCard.Card greyedOut={!pc.active} transparent={true}>
      <Box className="header-wrapper">
        <Box className="header-content">
          <S.ConfigurationSortCard.Header
            avatar={
              <S.ConfigurationSortCard.Box>
                <S.ConfigurationSortCard.MoveButtons>
                  <IconButton
                    color="inherit"
                    disabled={pcIndex === 0 || isReorderingLocked}
                    onClick={() =>
                      onSwitchPrinciplePosition(principlesSorting[pcIndex], principlesSorting[pcIndex - 1])
                    }
                    size="small"
                  >
                    <MoveUpIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    color="inherit"
                    disabled={pcIndex === principlesSorting.length - 1 || isReorderingLocked}
                    onClick={() =>
                      onSwitchPrinciplePosition(principlesSorting[pcIndex], principlesSorting[pcIndex + 1])
                    }
                    size="small"
                  >
                    <MoveDownIcon fontSize="small" />
                  </IconButton>
                </S.ConfigurationSortCard.MoveButtons>
              </S.ConfigurationSortCard.Box>
            }
            title={
              <Typography variant="h2" color={'textPrimary'} sx={{ flexGrow: 1 }}>
                {pc.names[locale]}
              </Typography>
            }
            action={
              <IconButton
                sx={{ m: 0, mr: -2 }}
                onClick={(e) => onEditPrincipleClick(e, pc.id)}
                color="primary"
                size={'small'}
              >
                <EditIcon fontSize={'small'} />
              </IconButton>
            }
            disableTypography
          />
        </Box>
      </Box>
    </S.ConfigurationSortCard.Card>
  )

  const renderSubPrincipleItem = (
    spc: Subprinciple_Config,
    spcIndex: number,
    subPrinciplesSorting: Subprinciple_Config[],
    locale: string,
    isReorderingLocked: boolean,
    onSwitchSubprinciplePosition: (a: Subprinciple_Config, b: Subprinciple_Config) => void,
  ) => (
    <Box mt={1} key={spc.id}>
      <S.ConfigurationSortCard.Card greyedOut={!spc.active}>
        <Box className="header-wrapper">
          <Box className="header-content">
            <S.ConfigurationSortCard.Header
              avatar={
                <S.ConfigurationSortCard.Box>
                  <S.ConfigurationSortCard.MoveButtons>
                    <IconButton
                      color="inherit"
                      disabled={spcIndex === 0 || isReorderingLocked}
                      onClick={() =>
                        onSwitchSubprinciplePosition(subPrinciplesSorting[spcIndex], subPrinciplesSorting[spcIndex - 1])
                      }
                      size="large"
                    >
                      <MoveUpIcon />
                    </IconButton>
                    <IconButton
                      color="inherit"
                      disabled={spcIndex === subPrinciplesSorting.length - 1 || isReorderingLocked}
                      onClick={() =>
                        onSwitchSubprinciplePosition(subPrinciplesSorting[spcIndex], subPrinciplesSorting[spcIndex + 1])
                      }
                      size="large"
                    >
                      <MoveDownIcon />
                    </IconButton>
                  </S.ConfigurationSortCard.MoveButtons>
                </S.ConfigurationSortCard.Box>
              }
              title={<Typography variant="subtitle2">{spc.names[locale]}</Typography>}
              action={
                <IconButton
                  sx={{ m: 0, mr: -2 }}
                  onClick={(e) => onEditSubPrincipleClick(e, spc.id)}
                  color="primary"
                  size={'small'}
                >
                  <EditIcon fontSize={'small'} />
                </IconButton>
              }
              disableTypography
            />
          </Box>
        </Box>
      </S.ConfigurationSortCard.Card>
    </Box>
  )
  return (
    <Stack spacing={7}>
      {principlesSorting.map((pcs, pcIndex) => {
        const subPrinciples = getSubprinciplesSorting(pcs)
        const hasSubPrinciples = subPrinciples.length > 0

        return (
          hasSubPrinciples && (
            <Box key={pcs.id}>
              {renderPrincipleHeader(
                pcs,
                pcIndex,
                principlesSorting,
                locale,
                isReorderingLocked,
                onSwitchPrinciplePosition,
              )}

              <Box pl={2}>
                {subPrinciples.map((spcs, spcIndex) =>
                  renderSubPrincipleItem(
                    spcs,
                    spcIndex,
                    subPrinciples,
                    locale,
                    isReorderingLocked,
                    onSwitchSubprinciplePosition,
                  ),
                )}
              </Box>
            </Box>
          )
        )
      })}
    </Stack>
  )
}
